import { config } from "@/config";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { apiFetch } from "@/lib/api-client";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { ProcessingStatus } from "@/types/chat";

// Run history (backend GET /ontology-runs): every generation run with who, when, status and what it cost. A run's
// cost is its setup (work done once: reading the document, roles, plan) + analysis (one part per ontology) + chat
// (each review chat message).

export interface RunCost {
  total: number;
  setup: number;
  analysis: number;
  chat: number;
}

export interface RunOntologyCost {
  ontology: string;
  status: string | null;
  model: string | null;
  cost: number;
  seconds: number;
  // input = every token sent (fresh + cached); cachedTokens = the cached part
  inputTokens: number;
  outputTokens: number;
  cachedTokens?: number;
}

export interface RunHistoryRow {
  runId: string;
  project: { uuid: string; name: string | null } | null;
  document: { id: number; name: string | null } | null;
  codeSync: { codeOntologyId: number; repository: string | null; baseCommitId: string | null; headCommitId: string | null } | null;
  agent: string | null;
  // V2 only: "standard" or "deep_search" (null on V1 runs and runs that predate it)
  functionalMode?: string | null;
  ontologies: string[];
  trigger: string | null;
  startedBy: { id: number | null; email: string | null; name?: string | null } | null;
  // the model(s) the analysis ran on
  models?: string[];
  retryOf: string | null;
  skipApproval: boolean;
  status: string;
  decision: { type: string; by: string | null; at: string | null } | null;
  startedAt: string | null;
  finishedAt: string | null;
  cost: RunCost;
  tokens: { input: number; output: number; cached?: number };
  seconds: number;
  chatMessages: number;
  perOntology: RunOntologyCost[];
}

export interface RunHistoryChatMessage {
  at: string | null;
  by: string | null;
  message: string | null;
  intent: string | null;
  revision: number | null;
  // each node a minor edit changed ("modified 'X': Renamed to 'Y'"); empty for a question or a major re-analysis
  changed: string[];
  // the re-analysis a major message started (the same message as the row before it)
  followUp?: boolean;
  cost: number;
  seconds: number;
  inputTokens: number;
  outputTokens: number;
}

export interface RunHistoryDetails extends RunHistoryRow {
  chat: RunHistoryChatMessage[];
}

export interface RunHistoryTotals {
  runs: number;
  cost: RunCost;
  tokens: { input: number; output: number; cached?: number };
  seconds: number;
  byStatus: Record<string, number>;
}

export interface RunHistoryPage {
  data: RunHistoryRow[];
  page: number;
  limit: number;
  total: number;
  from: string | null;
  to: string | null;
  totals?: RunHistoryTotals;
  projects?: { uuid: string; name: string | null }[];
}

export type RunHistoryStatus =
  | "running"
  | "review"
  | "revising"
  | "applying"
  | "applied"
  | "rejected"
  | "cancelled"
  | "error";

export interface RunHistoryFilters {
  projectUuid?: string;
  ontology?: string;
  status?: RunHistoryStatus;
  from?: string; // yyyy-mm-dd
  to?: string; // yyyy-mm-dd, inclusive
  sortName?: "startedAt" | "cost" | "tokens" | "seconds";
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
}

export class RunHistoryError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function getJson<T>(url: string, headers: Record<string, string>): Promise<T> {
  const response = await apiFetch(url, { headers });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body?.message || body?.error || message;
    } catch {
      // not JSON
    }
    throw new RunHistoryError(message, response.status);
  }
  return response.json();
}

function historyUrl(f: RunHistoryFilters): string {
  const q = new URLSearchParams({ withTotals: "true" });
  if (f.projectUuid) q.set("projectUuid", f.projectUuid);
  if (f.ontology) q.set("ontology", f.ontology);
  if (f.status) q.set("status", f.status);
  if (f.from) q.set("from", new Date(`${f.from}T00:00:00`).toISOString());
  if (f.to) {
    // inclusive end date: up to the start of the next day
    const end = new Date(`${f.to}T00:00:00`);
    end.setDate(end.getDate() + 1);
    q.set("to", end.toISOString());
  }
  if (f.sortName) q.set("sortName", f.sortName);
  if (f.sortOrder) q.set("sortOrder", f.sortOrder);
  q.set("page", String(f.page ?? 1));
  q.set("limit", String(f.limit ?? 20));
  return `${config.isometricApiUrl}/ontology-runs?${q.toString()}`;
}

/** One page of run history, with the totals over every matching run and the projects to filter by. */
export function useRunHistory(filters: RunHistoryFilters) {
  const { getAuthHeaders } = useAuthHeaders();
  return useQuery({
    queryKey: ["run-history", filters],
    queryFn: () => getJson<RunHistoryPage>(historyUrl(filters), getAuthHeaders()),
    placeholderData: keepPreviousData,
    retry: (count, error) => !(error instanceof RunHistoryError && error.status === 403) && count < 2,
  });
}

/**
 * One run as history shows it (cost split, each chat message). `refreshKey` (e.g. the run's status) refetches when the
 * run moves on, instead of polling.
 */
export function useRunDetails(runId: string | null | undefined, opts: { enabled?: boolean; refreshKey?: unknown } = {}) {
  const { getAuthHeaders } = useAuthHeaders();
  return useQuery({
    queryKey: ["run-details", runId, opts.refreshKey ?? null],
    enabled: (opts.enabled ?? true) && !!runId,
    queryFn: () =>
      getJson<RunHistoryDetails>(`${config.isometricApiUrl}/ontology-runs/${runId}/details`, getAuthHeaders()),
    retry: false,
    staleTime: 30_000,
  });
}

/** One pass of a run's trace: the analysis, then each chat revision. */
export interface RunTracePass {
  id: string;
  label: string;
  at: string;
  steps: TraceStep[];
  truncated: boolean;
  failed: boolean;
}

export interface RunTrace {
  runId: string;
  setup: { passes: RunTracePass[] } | null;
  ontologies: { jobId: string; ontology: string; trace: { passes: RunTracePass[] } | null }[];
}

/** How a run ran (backend GET /ontology-runs/:runId/trace): large, so loaded only when asked for. */
export function useRunTrace(runId: string, enabled: boolean) {
  const { getAuthHeaders } = useAuthHeaders();
  return useQuery({
    queryKey: ["run-trace", runId],
    enabled,
    queryFn: () => getJson<RunTrace>(`${config.isometricApiUrl}/ontology-runs/${runId}/trace`, getAuthHeaders()),
    retry: false,
    staleTime: 60_000,
  });
}

// "us.anthropic.claude-sonnet-4-6" -> "sonnet-4-6"
export const shortModel = (model: string | null | undefined): string =>
  model ? model.replace(/^.*?claude-/, "").replace(/-\d{8}(-v\d+(:\d+)?)?$/, "").replace(/-v\d+(:\d+)?$/, "") : "—";

/** Shown wherever a run or chat cost appears: how the number is made, and that it is still being checked. */
export const COST_NOTE =
  "Calculated from each model call's token counts and the model's price. Still being validated: it can differ from the AWS bill.";

export const formatCost = (n: number | null | undefined): string =>
  n == null ? "—" : n >= 100 ? `$${n.toFixed(0)}` : n >= 1 ? `$${n.toFixed(2)}` : `$${n.toFixed(3)}`;

export const formatTokens = (n: number | null | undefined): string => {
  if (!n) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
};

export const formatDuration = (seconds: number | null | undefined): string => {
  if (!seconds) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.round(seconds / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`;
};

// ── Chat history (backend GET /chat-usage): one row per normal / deep chat exchange — the question and its answer —
// with what answering cost. Same access as runs: platform admins every project, project owners theirs.

export type ChatUsageMode = "normal" | "deep";

export interface ChatUsageRow {
  id: number;
  at: string | null;
  project: { uuid: string; name: string | null };
  thread: { id: string; title: string | null } | null;
  user: { id: number | null; email: string | null; name: string | null };
  mode: ChatUsageMode;
  status: string | null;
  model: string | null;
  tokens: { input: number; output: number; cached: number };
  cost: number;
  seconds: number;
  // a preview in the list; the whole text from useChatUsageDetails
  question: string | null;
  questionChars: number;
  answer: string | null;
  answerChars: number;
  // how the answer was produced; only on useChatUsageDetails (null for chats from before it was recorded)
  trace?: ChatTrace | null;
}

/** One step of a chat answer or a run: a tool call (request / response, cut), a model call (tokens, $), or an error. */
export interface TraceStep {
  step: number;
  kind: "tool" | "model" | "error";
  // the part that ran it: a deep-analysis specialist (chat), an ontology or setup (run)
  stage?: string;
  name?: string;
  input?: string;
  inputChars?: number;
  output?: string;
  outputChars?: number;
  ok?: boolean;
  error?: string;
  ms?: number;
  model?: string | null;
  inputTokens?: number;
  outputTokens?: number;
  cachedTokens?: number;
  cost?: number;
  toolsAsked?: string[];
  stop?: string | null; // why a model call stopped (max_tokens = cut off)
}

/** @deprecated use TraceStep */
export type ChatTraceStep = TraceStep;

export interface ChatTrace {
  steps: TraceStep[];
  truncated: boolean;
  failed?: boolean;
}

export interface ChatUsageTotals {
  chats: number;
  cost: number;
  tokens: { input: number; output: number; cached: number };
  seconds: number;
  notAnswered: number;
}

export interface ChatUsagePage {
  data: ChatUsageRow[];
  page: number;
  limit: number;
  total: number;
  totals?: ChatUsageTotals;
  projects?: { uuid: string; name: string | null }[];
}

export interface ChatUsageFilters {
  mode?: ChatUsageMode; // both when unset
  status?: ProcessingStatus;
  projectUuid?: string;
  from?: string; // yyyy-mm-dd
  to?: string; // yyyy-mm-dd, inclusive
  sortName?: "createdAt" | "cost" | "tokens" | "seconds";
  page?: number;
  limit?: number;
}

function chatUsageUrl(f: ChatUsageFilters): string {
  const q = new URLSearchParams({ withTotals: "true" });
  if (f.mode) q.set("mode", f.mode);
  if (f.status) q.set("status", f.status);
  if (f.projectUuid) q.set("projectUuid", f.projectUuid);
  if (f.from) q.set("from", new Date(`${f.from}T00:00:00`).toISOString());
  if (f.to) {
    const end = new Date(`${f.to}T00:00:00`);
    end.setDate(end.getDate() + 1);
    q.set("to", end.toISOString());
  }
  if (f.sortName) q.set("sortName", f.sortName);
  q.set("sortOrder", "desc");
  q.set("page", String(f.page ?? 1));
  q.set("limit", String(f.limit ?? 20));
  return `${config.isometricApiUrl}/chat-usage?${q.toString()}`;
}

export function useChatUsage(filters: ChatUsageFilters) {
  const { getAuthHeaders } = useAuthHeaders();
  return useQuery({
    queryKey: ["chat-usage", filters],
    queryFn: () => getJson<ChatUsagePage>(chatUsageUrl(filters), getAuthHeaders()),
    placeholderData: keepPreviousData,
    retry: (count, error) => !(error instanceof RunHistoryError && error.status === 403) && count < 2,
  });
}

/** One exchange with its whole question and answer, and its trace (loaded when a row is opened; the chat screen never
 *  loads the trace). */
export function useChatUsageDetails(id: number | null) {
  const { getAuthHeaders } = useAuthHeaders();
  return useQuery({
    queryKey: ["chat-usage-details", id],
    enabled: id != null,
    queryFn: () => getJson<ChatUsageRow>(`${config.isometricApiUrl}/chat-usage/${id}`, getAuthHeaders()),
    retry: false,
    staleTime: 5 * 60_000,
  });
}
